import { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import api from '../services/api'

export default function ResetPassword() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!token) {
      setError('Missing reset token. Please use the link from your email.')
      return
    }
    if (password.length < 12) {
      setError('Password must be at least 12 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, password })
      setDone(true)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Reset failed. Your link may have expired — please request a new one.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/logo-mark.svg" alt="HSEQ Intelligence" className="mx-auto mb-4" style={{ width: 72, height: 72 }} />
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Choose a new password</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Must be at least 12 characters.
          </p>
        </div>

        <div className="card">
          {done ? (
            <div className="text-center space-y-4 py-2">
              <div className="text-4xl">✅</div>
              <p className="font-semibold" style={{ color: 'var(--text)' }}>Password updated</p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Your password has been changed. All other sessions have been signed out.
              </p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="btn btn-primary w-full justify-center"
                style={{ display: 'flex' }}
              >
                Sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {!token && (
                <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--red-bg, #fef2f2)', color: 'var(--red)' }}>
                  This link is invalid. Please use the link from your email.
                </div>
              )}
              <div>
                <label>New password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={12}
                  autoComplete="new-password"
                  placeholder="At least 12 characters"
                  disabled={!token}
                />
              </div>
              <div>
                <label>Confirm new password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="Repeat your new password"
                  disabled={!token}
                />
              </div>
              {error && (
                <p className="text-sm" style={{ color: 'var(--red)' }}>{error}</p>
              )}
              <button type="submit" className="btn btn-primary w-full justify-center" disabled={loading || !token}>
                {loading ? 'Updating...' : 'Set new password'}
              </button>
            </form>
          )}

          {!done && (
            <p className="text-center text-sm mt-4" style={{ color: 'var(--text-muted)' }}>
              Remember it?{' '}
              <Link to="/login" style={{ color: 'var(--accent)' }} className="font-medium">Back to login</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
